/**
 * Wall-clock timing for one assistant turn, shared by web and native.
 *
 * The stream carries no timestamps, so the clock is the client's: it starts the
 * first time a message renders as `running` and stops the moment it no longer
 * is. A message rehydrated from history was never running on this device, so
 * it gets no timing at all — the header then says "Ran 3 steps" rather than
 * inventing a duration it cannot know.
 *
 * The elapsed label ticks at 10 Hz in tenths of a second, mono tabular figures,
 * exactly like the loader it sits beside: "4.2s", then "1m 3.4s".
 */
import { useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';

export type RunTiming = {
  /** Epoch ms when this message first rendered as running; null if never. */
  startedAt: number | null;
  /** Epoch ms when it stopped running; null while running or if never ran. */
  settledAt: number | null;
};

export function useRunTiming(isRunning: boolean): RunTiming {
  const [timing, setTiming] = useState<RunTiming>(() => ({
    startedAt: isRunning ? Date.now() : null,
    settledAt: null,
  }));
  const wasRunning = useRef(isRunning);

  useEffect(() => {
    if (isRunning && !wasRunning.current) {
      setTiming({ startedAt: Date.now(), settledAt: null });
    } else if (!isRunning && wasRunning.current) {
      setTiming((prev) => (prev.startedAt ? { ...prev, settledAt: Date.now() } : prev));
    }
    wasRunning.current = isRunning;
  }, [isRunning]);

  return timing;
}

/**
 * Below this a "run" was not a run this device watched: a rehydrated message
 * can flash through `running` for a frame while the thread store settles, and
 * a real turn never returns in under half a second. Treat it as unknown rather
 * than report "in 0s".
 */
const MIN_TRUSTED_DURATION_MS = 500;

export function runDurationMs(timing: RunTiming): number | null {
  if (timing.startedAt == null || timing.settledAt == null) return null;
  const duration = timing.settledAt - timing.startedAt;
  return duration >= MIN_TRUSTED_DURATION_MS ? duration : null;
}

/** "4.2s" under a minute, "1m 3.4s" above it. Tenths, so the figure visibly moves. */
export function formatElapsed(ms: number, t: TFunction): string {
  const total = Math.max(0, ms) / 1000;
  if (total < 60) {
    return t('chat.activity.elapsed.seconds', { value: total.toFixed(1) });
  }
  const minutes = Math.floor(total / 60);
  const seconds = (total % 60).toFixed(1);
  return t('chat.activity.elapsed.minutes', { minutes, seconds });
}

/** A whole-second variant for settled labels: "12s", "1m 4s". */
export function formatDuration(ms: number, t: TFunction): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) {
    return t('chat.activity.elapsed.seconds', { value: String(total) });
  }
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60);
  return t('chat.activity.elapsed.minutes', { minutes, seconds });
}

/**
 * Live elapsed label. Ticks only while `running`; once stopped it freezes on
 * the final value rather than unmounting, so the number a user was watching
 * does not vanish the instant the work ends.
 */
export function useElapsedLabel(
  startedAt: number | null,
  running: boolean,
  t: TFunction,
): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running || startedAt == null) return;
    // On web the clock stops ticking while the tab is hidden and catches up
    // on return: elapsed time is wall-clock, not a count of ticks, so nothing
    // is lost. Native has no `document`; it simply keeps the interval.
    const doc = typeof document === 'undefined' ? null : document;
    let timer: ReturnType<typeof setInterval> | undefined;
    const sync = () => {
      if (timer) clearInterval(timer);
      timer = undefined;
      setNow(Date.now());
      if (!doc || !doc.hidden) timer = setInterval(() => setNow(Date.now()), 100);
    };
    sync();
    doc?.addEventListener('visibilitychange', sync);
    return () => {
      if (timer) clearInterval(timer);
      doc?.removeEventListener('visibilitychange', sync);
    };
  }, [running, startedAt]);

  if (startedAt == null) return null;
  return formatElapsed(now - startedAt, t);
}
